import { ArrowRightLeft, Ban, CheckCircle2, ClipboardCheck, Clock, Download, FileText, History, Package, PackageCheck, Pencil, Settings, ShieldCheck, ShieldX, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable } from '../../../../components/data-table/useDataTable';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { EvidenceItemModal } from '../components/EvidenceItemModal';
import { EvidenceEvaluateModal } from '../components/EvidenceEvaluateModal';
import { EvidenceMarkingsModal } from '../components/EvidenceMarkingsModal';
import { evaluationAuditRows } from '../evaluationAudit';
import { EvidenceMovementModal } from '../components/EvidenceMovementModal';
import { EvidenceStoreInModal } from '../components/EvidenceStoreInModal';
import { evidenceItemsApi } from '../evidenceApi';
import {
  CUSTODY,
  CUSTODY_LABEL,
  FLAG,
  ITEM_KIND,
  MOVEMENT,
  MOVEMENT_LABEL,
  type EvidenceMovementType,
  hasFlag,
  type EvidenceAttachmentItem,
  type EvidenceChainVerification,
  type EvidenceItemDetail,
  type EvidenceMovementItem,
  DESTINATION,
  DESTINATION_LABEL,
} from '../evidenceTypes';
import { personnelLabel, usePersonName, usePersonnelNames } from '../../../human-resources/personnel/useStaff';
import { saveBlob } from '../utils/saveBlob';

type TabKey = 'info' | 'chain' | 'documents';

/**
 * One item of evidence and its chain of custody.
 *
 * <b>Opening this page is LOGGED</b> server-side. Every download is logged too, and every
 * download is re-hashed and refused if the bytes no longer match the fingerprint taken at intake.
 *
 * <b>Where it is and who holds it change only by a custody line</b> — a movement, approved and
 * password-confirmed, or a stored-in. The Custody card's cog offers those; nothing else on the
 * page can move the item.
 */
/** `EvidenceMovementRequestStatus` — the words the approval module uses. */
const TRANSFER_STATUS_LABEL: Record<number, string> = {
  0: 'Pending',
  1: 'Approved',
  2: 'Rejected',
  3: 'Retracted',
};

export function EvidenceItemProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const canDownload = usePermission('evidence-items.download');

  const [detail, setDetail] = useState<EvidenceItemDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('info');
  const [modal, setModal] = useState<'edit' | 'markings' | 'evaluate' | 'move' | 'storeIn' | 'history' | 'delete' | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [verification, setVerification] = useState<EvidenceChainVerification | null>(null);
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      evidenceItemsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setDetail(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this evidence.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    setDetail(null);
    setError(null);
    setVerification(null);

    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const personnelIds = useMemo(
    () =>
      detail
        ? [
            detail.item.agentPersonnelId,
            detail.item.submittingAgentPersonnelId,
            detail.item.evaluatedByPersonnelId,
            detail.item.storedByPersonnelId,
            detail.item.custodianPersonnelId,
            detail.transfer?.requestorPersonnelId,
            detail.pendingMovement?.toCustodianPersonnelId,
            ...detail.movements.flatMap((m) => [m.fromCustodianPersonnelId, m.toCustodianPersonnelId]),
          ]
        : [],
    [detail],
  );

  const { names, canRead } = usePersonnelNames(personnelIds);
  // Above every early return — a hook is called on every render or on none.
  const judge = usePersonName(detail?.transfer?.judgePersonId);

  const breadcrumb: BreadcrumbSegment[] = [
    'Evidence Management',
    { label: 'Evidence', to: '/evidence/cases' },
    ...(detail ? [{ label: detail.item.caseNumber, to: `/evidence/cases/${detail.item.evidenceCaseId}` }] : []),
    detail?.item.inventoryNumber ?? 'Item',
  ];

  if (error && !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        {error.isForbidden ? (
          <EmptyState message="This evidence belongs to a case above your access level. It can be opened only by members cleared for that level." />
        ) : (
          <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
        )}
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { item, pendingMovement } = detail;
  const flags = item.permissions;
  const isDigital = item.kind === ITEM_KIND.digital;

  const moveBlocked = !item.isActive
    ? 'This item is deleted.'
    : item.status === CUSTODY.forEvaluation
      ? 'It is not in storage yet — accept it at evaluation, from the case\'s Evidence tab, first.'
      : item.status === CUSTODY.deferred
        ? 'It is held outside the facility. Store it in from the Custody card first.'
      : item.status !== CUSTODY.stored
        ? 'Only evidence in storage can be moved, transferred or disposed of.'
        : pendingMovement
          ? 'A movement of this item is already waiting for approval.'
          : !hasFlag(flags, FLAG.edit)
            ? 'You have not been given Edit on this case.'
            : null;

  const storeInBlocked = !item.isActive
    ? 'This item is deleted.'
    : item.status !== CUSTODY.transferred && item.status !== CUSTODY.deferred
      ? item.status === CUSTODY.disposed
        ? 'This evidence has been disposed of.'
        : item.status === CUSTODY.forEvaluation
          ? 'It is stored in by accepting it at evaluation, from the case\'s Evidence tab.'
          : 'This evidence is already in storage.'
      : !hasFlag(flags, FLAG.upload)
        ? 'You have not been given Upload on this case.'
        : null;

  async function download(fetch: () => Promise<{ blob: Blob; fileName: string | null }>, fallback: string) {
    setActionError(null);

    try {
      const { blob, fileName } = await fetch();
      saveBlob(blob, fileName ?? fallback);
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The file could not be downloaded.', 0));
    }
  }

  async function verify() {
    if (!id) return;

    setVerifying(true);
    setActionError(null);

    try {
      setVerification(await evidenceItemsApi.verifyChain(id));
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The chain could not be verified.', 0));
    } finally {
      setVerifying(false);
    }
  }

  async function deactivate(reauthentication?: string) {
    if (!id) return;

    setActing(true);
    setActionError(null);

    try {
      await evidenceItemsApi.deactivate(id, reauthentication ?? '');
      toast.success(`${item.inventoryNumber} was deleted.`);
      setModal(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The item could not be deleted.', 0));
    } finally {
      setActing(false);
    }
  }

  const editReason = !item.isActive
    ? 'This item is deleted.'
    : !hasFlag(flags, FLAG.edit)
      ? 'You have not been given Edit on its case.'
      : null;

  /*
    THE SAME ACTIONS AS THE CASE'S RECEIVED ROW — product call, 08 Oct 2026: evaluate, edit,
    delete — with History Logs before Delete, which
    stays last. Each carries the key of what it does, and the case's flag or the item's state
    disables it with the reason.
  */
  // NOT OFFERED on a stored, transferred or disposed item (08 Oct 2026) — the case table's rule.
  // ONLY what WAITS (09 Oct 2026); a deferred item comes back by Store-in.
  const evaluable = item.status === CUSTODY.forEvaluation;

  const actions: MenuItem[] = [
    ...(evaluable
      ? [
          {
            key: 'evaluate',
            label: 'Evaluate',
            icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
            permission: 'evidence-items.create',
            disabledReason: !item.isActive
              ? 'This item is deleted.'
              : !hasFlag(flags, FLAG.upload)
                ? 'You have not been given Upload on its case.'
                : null,
            onClick: () => setModal('evaluate'),
          },
        ]
      : []),
    {
      key: 'edit',
      label: 'Edit',
      icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
      permission: 'evidence-items.update',
      disabledReason: editReason,
      onClick: () => setModal('edit'),
    },
    {
      key: 'history',
      label: 'History Logs',
      icon: <History className="h-4 w-4" aria-hidden="true" />,
      permission: 'audit-logs.read',
      onClick: () => setModal('history'),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
      permission: 'evidence-items.delete',
      destructive: true,
      disabledReason: !item.isActive
        ? 'This item is already deleted.'
        : !hasFlag(flags, FLAG.delete)
          ? 'You have not been given Allow delete on its case.'
          : pendingMovement
            ? 'A movement of this item is waiting for approval.'
            : item.receiptNumber != null
              ? `This evidence is acknowledged on receipt ${item.receiptNumber}, which cannot be changed.`
              : item.status === CUSTODY.stored && item.movementCount > 1
              ? 'This evidence is in storage and has moved. It leaves by a transfer or a disposal, not a delete.'
              : item.status === CUSTODY.deferred && item.movementCount > 1
                ? 'This evidence was deferred and is held outside the facility. It is not removed by a delete.'
                : null,
      onClick: () => setModal('delete'),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="evidence-item-profile"
        panel={tab === 'info' ? 'cards' : 'bleed'}
        header={
          <ProfileHeader
            id="evidence-item-profile-header"
            icon={isDigital ? <FileText className="h-6 w-6" aria-hidden="true" /> : <Package className="h-6 w-6" aria-hidden="true" />}
            title={item.inventoryNumber}
            meta={[item.caseNumber, item.kindName, CUSTODY_LABEL[item.status]]}
            status={<StatusText tone={item.isActive ? 'active' : 'inactive'}>{item.isActive ? 'Active' : 'Deleted'}</StatusText>}
            createdBy={item.createdByName}
            createdAt={item.createdAt}
            actions={
              <Menu
                id="evidence-item-profile-settings"
                items={actions}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    id="evidence-item-profile-settings-trigger"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Evidence settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          actionError ? (
            <ProfileBanner id="evidence-item-profile-banner" tone="red" icon={<Ban className="h-4 w-4" aria-hidden="true" />} message={actionError.message} />
          ) : !item.isActive ? (
            <ProfileBanner
              id="evidence-item-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This item is deleted. Its chain of custody and documents are kept."
            />
          ) : pendingMovement ? (
            <ProfileBanner
              id="evidence-item-profile-banner"
              tone="amber"
              icon={<Clock className="h-4 w-4" aria-hidden="true" />}
              message={`${MOVEMENT_LABEL[pendingMovement.movementType]} requested by ${pendingMovement.requestedByName ?? 'a member'} is waiting for approval. Nothing moves until it is approved.`}
            />
          ) : item.status === CUSTODY.disposed ? (
            <ProfileBanner
              id="evidence-item-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This evidence has been disposed of. Nothing further can be recorded against it."
            />
          ) : item.status === CUSTODY.forEvaluation ? (
            <ProfileBanner
              id="evidence-item-profile-banner"
              tone="amber"
              icon={<Clock className="h-4 w-4" aria-hidden="true" />}
              message="Received and waiting for evaluation — accept it into storage or defer it, from the case's Evidence tab."
            />
          ) : item.status === CUSTODY.deferred ? (
            <ProfileBanner
              id="evidence-item-profile-banner"
              tone="blue"
              icon={<ArrowRightLeft className="h-4 w-4" aria-hidden="true" />}
              message="Deferred at evaluation and held outside the facility — the place is on its chain of custody. When it comes back, store it in from the Custody card."
            />
          ) : item.status === CUSTODY.transferred ? (
            <ProfileBanner
              id="evidence-item-profile-banner"
              tone="blue"
              icon={<ArrowRightLeft className="h-4 w-4" aria-hidden="true" />}
              message="This evidence has been transferred out. When it comes back, store it in from the Custody card."
            />
          ) : (
            <ProfileBanner
              id="evidence-item-profile-banner"
              tone="slate"
              icon={<PackageCheck className="h-4 w-4" aria-hidden="true" />}
              message={
                `In storage at ${item.storageLocationName ?? 'its location'}. Moving it is requested from the Custody card and needs approval.`
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          { key: 'chain', label: <>Chain of custody <CountBadge count={detail.movements.length} /></> },
          { key: 'documents', label: <>Documents <CountBadge count={detail.attachments.length} /></> },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="evidence-item-profile-details"
              title="Evidence"
              menuItems={[
                {
                  key: 'edit',
                  label: 'Edit',
                  permission: 'evidence-items.update',
                  disabledReason: !item.isActive
                    ? 'This item is deleted.'
                    : hasFlag(flags, FLAG.edit)
                      ? null
                      : 'You have not been given Edit on its case.',
                  onClick: () => setModal('edit'),
                },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Inventory No.', value: item.inventoryNumber, mono: true },
                  { label: 'Case', value: item.caseNumber, mono: true },
                  { label: 'Container/Box No', value: item.containerNumber, mono: true },
                  { label: 'Kind', value: item.kindName },
                  { label: 'Type', value: item.typeName },
                  { label: 'Description', value: item.description },
                  {
                    label: 'Acquired',
                    value: item.acquiredAt ? (
                      <time dateTime={toIso(item.acquiredAt)} className="font-mono">
                        {formatDateTime(item.acquiredAt)}
                      </time>
                    ) : null,
                  },
                  { label: 'Acquired by', value: personnelLabel(item.agentPersonnelId, names, canRead) },
                  { label: 'Agent', value: personnelLabel(item.submittingAgentPersonnelId, names, canRead) },
                ]}
              />
            </ProfileCard>

            {/*
              EVALUATION — its audit (08 Oct 2026). A card moved by an ACTION, not by typing, so its
              cog carries Evaluate rather than Edit — the header menu's own entry, gated the same.
            */}
            <ProfileCard
              id="evidence-item-profile-evaluation"
              title="Evaluation"
              menuItems={actions.filter((action) => action.key === 'evaluate')}
            >
              <PreviewTable variant="table-display" rows={evaluationAuditRows(item, (id) => personnelLabel(id, names, canRead))} />
            </ProfileCard>

            {/*
              MARKINGS — set by hand after receiving (product call, 08 Oct 2026). The evidence status
              here is a LABEL; custody, on the card below, moves only by an approved movement.
            */}
            <ProfileCard
              id="evidence-item-profile-markings"
              title="Markings"
              menuItems={[
                {
                  key: 'edit',
                  label: 'Edit',
                  permission: 'evidence-items.update',
                  disabledReason: !item.isActive
                    ? 'This item is deleted.'
                    : hasFlag(flags, FLAG.edit)
                      ? null
                      : 'You have not been given Edit on its case.',
                  onClick: () => setModal('markings'),
                },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Purpose', value: item.purpose },
                ]}
              />
            </ProfileCard>

            {/* A card whose state is moved by an ACTION, never by typing — so its cog carries no Edit. */}
            <ProfileCard
              id="evidence-item-profile-custody"
              title="Custody"
              menuItems={[
                {
                  key: 'move',
                  label: 'Request movement',
                  icon: <ArrowRightLeft className="h-4 w-4" aria-hidden="true" />,
                  permission: 'evidence-items.move',
                  disabledReason: moveBlocked,
                  onClick: () => setModal('move'),
                },
                {
                  key: 'store-in',
                  label: 'Store in',
                  icon: <PackageCheck className="h-4 w-4" aria-hidden="true" />,
                  permission: 'evidence-items.create',
                  disabledReason: storeInBlocked,
                  onClick: () => setModal('storeIn'),
                },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Custody', value: CUSTODY_LABEL[item.status] },
                  { label: 'Custodian', value: personnelLabel(item.custodianPersonnelId, names, canRead) },
                  {
                    label: 'Storage location',
                    value: item.storageLocationName,
                  },
                  { label: 'Location code', value: item.storageLocationCode, mono: true },
                  {
                    label: 'Awaiting approval',
                    value: pendingMovement ? MOVEMENT_LABEL[pendingMovement.movementType] : null,
                  },
                ]}
              />
            </ProfileCard>

            {/*
              THE TRANSFER — the latest court or disposal batch this item rode in (08 Oct 2026), pending
              or decided. One approval covers the batch, so the item count says how many it moved with.
            */}
            {detail.transfer ? (
              <ProfileCard id="evidence-item-profile-transfer" title="Transfer">
                <PreviewTable
                  variant="table-display"
                  rows={[
                    { label: 'To', value: DESTINATION_LABEL[detail.transfer.destination] },
                    { label: 'Approval', value: TRANSFER_STATUS_LABEL[detail.transfer.status] ?? null },
                    ...(detail.transfer.destination === DESTINATION.court
                      ? [
                          { label: 'Court branch', value: detail.transfer.courtBranch ?? null },
                          { label: 'Court Order No.', value: detail.transfer.courtOrderNumber ?? null, mono: true },
                          { label: 'Judge', value: judge },
                          { label: 'Requestor', value: personnelLabel(detail.transfer.requestorPersonnelId, names, canRead) },
                        ]
                      : [
                          { label: 'Disposal type', value: detail.transfer.disposalTypeName ?? null },
                          { label: 'Reason', value: detail.transfer.reason ?? null },
                        ]),
                    { label: 'Items in the transfer', value: String(detail.transfer.itemCount), mono: true },
                    {
                      label: 'Requested',
                      value: (
                        <time dateTime={toIso(detail.transfer.requestedAt)} className="font-mono">
                          {formatDateTime(detail.transfer.requestedAt)}
                        </time>
                      ),
                    },
                    { label: 'Requested by', value: detail.transfer.requestedByName ?? null },
                  ]}
                />
              </ProfileCard>
            ) : null}

            {isDigital ? (
              <ProfileCard
                id="evidence-item-profile-file"
                title="Evidence file"
                menuItems={[
                  {
                    key: 'download',
                    label: 'Download',
                    icon: <Download className="h-4 w-4" aria-hidden="true" />,
                    permission: 'evidence-items.download',
                    onClick: () => void download(() => evidenceItemsApi.downloadFile(item.id), item.fileName ?? 'evidence'),
                  },
                ]}
              >
                <PreviewTable
                  variant="table-display"
                  rows={[
                    { label: 'File name', value: item.fileName },
                    { label: 'Type', value: detail.fileContentType, mono: true },
                    {
                      label: 'Size',
                      value: item.fileSizeBytes != null ? <span className="font-mono">{formatBytes(item.fileSizeBytes)}</span> : null,
                    },
                    {
                      label: 'Fingerprint (SHA-256)',
                      value: detail.fileSha256 ? <span className="break-all font-mono text-xs">{detail.fileSha256}</span> : null,
                    },
                  ]}
                />
              </ProfileCard>
            ) : null}

            {/* Derived from the chain — nothing to type, so its cog runs the check instead. */}
            <ProfileCard
              id="evidence-item-profile-integrity"
              title="Chain integrity"
              menuItems={[
                {
                  key: 'verify',
                  label: verifying ? 'Verifying…' : 'Verify chain',
                  icon: <ShieldCheck className="h-4 w-4" aria-hidden="true" />,
                  permission: 'evidence-items.read',
                  disabledReason: verifying ? 'A check is running.' : null,
                  onClick: () => void verify(),
                },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    label: 'Result',
                    value: verification ? (
                      verification.intact ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-700">
                          <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Every line verifies
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-red-700">
                          <ShieldX className="h-4 w-4" aria-hidden="true" /> Broken at line {verification.brokenAtSequence}
                        </span>
                      )
                    ) : (
                      'Not checked on this visit'
                    ),
                  },
                  {
                    label: 'Lines',
                    value: <span className="font-mono tabular-nums">{verification?.lineCount ?? detail.movements.length}</span>,
                  },
                  {
                    label: 'Newest line',
                    value: verification?.headHash ? <span className="break-all font-mono text-xs">{verification.headHash}</span> : null,
                  },
                  {
                    label: 'Checked',
                    value: verification ? (
                      <time dateTime={toIso(verification.verifiedAt)} className="font-mono">
                        {formatDateTime(verification.verifiedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="evidence-item-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: item.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(item.createdAt)} className="font-mono">
                        {formatDateTime(item.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: item.updatedByName },
                  {
                    label: 'Updated',
                    value: item.updatedAt ? (
                      <time dateTime={toIso(item.updatedAt)} className="font-mono">
                        {formatDateTime(item.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : null}

        {tab === 'chain' ? <ChainOfCustody movements={detail.movements} names={names} canRead={canRead} /> : null}

        {tab === 'documents' ? (
          <Documents
            attachments={detail.attachments}
            canDownload={canDownload}
            onDownload={(attachment) =>
              void download(() => evidenceItemsApi.downloadAttachment(item.id, attachment.id), attachment.fileName)
            }
          />
        ) : null}
      </ProfileHeaderShell>

      {modal === 'edit' ? (
        <EvidenceItemModal
          item={item}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            setModal(null);
            setDetail(saved);
            toast.success(`${item.inventoryNumber} was saved.`);
          }}
        />
      ) : null}

      {modal === 'evaluate' ? (
        <EvidenceEvaluateModal
          items={[item]}
          onClose={() => setModal(null)}
          onSaved={(evaluated) => {
            toast.success(evaluated.length === 1 ? `${item.inventoryNumber} was evaluated.` : `${evaluated.length} items were evaluated.`);
            setModal(null);
            load();
          }}
        />
      ) : null}

      {modal === 'markings' ? (
        <EvidenceMarkingsModal
          item={item}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            setModal(null);
            setDetail(saved);
            toast.success(`${item.inventoryNumber}'s markings were saved.`);
          }}
        />
      ) : null}

      {modal === 'move' ? (
        <EvidenceMovementModal
          item={item}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            setModal(null);
            setDetail(saved);
            toast.success('The movement was requested. It is waiting for approval.');
          }}
        />
      ) : null}

      {modal === 'storeIn' ? (
        <EvidenceStoreInModal
          item={item}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            setModal(null);
            setDetail(saved);
            toast.success(`${item.inventoryNumber} was stored in.`);
          }}
        />
      ) : null}

      {modal === 'history' ? (
        <HistoryLogsModal open onClose={() => setModal(null)} module="evidence-items" entityId={item.id} />
      ) : null}

      {modal === 'delete' ? (
        <ConfirmDialog
          open
          onClose={() => setModal(null)}
          onConfirm={(reauthentication) => void deactivate(reauthentication)}
          title="Delete evidence item"
          danger
          requireConfirm
          requireCredential
          loading={acting}
          id="evidence-item-profile-deactivate-dialog"
          body={
            <p>
              {item.inventoryNumber} is hidden from the evidence list. A delete corrects a record
              entered in error; evidence that has moved leaves by a transfer or a disposal, and the
              server refuses a delete for it. Nothing is erased.
            </p>
          }
        />
      ) : null}
    </div>
  );
}

function ChainOfCustody({
  movements,
  names,
  canRead,
}: {
  movements: EvidenceMovementItem[];
  names: Record<string, string>;
  canRead: boolean;
}) {
  // The chain arrives WHOLE with the item, so the table is handed it as one page.
  const fetchPage = useCallback(async () => ({ items: movements, total: movements.length }), [movements]);
  const state = useDataTable<EvidenceMovementItem>({ fetchPage, pageSize: 50 });
  const columns = useMemo(() => chainColumns(names, canRead), [names, canRead]);

  return (
    <div className="flex h-full min-w-0 flex-col p-4">
      <DataTablePanel
        id="evidence-item-chain"
        state={state}
        columns={columns}
        rowKey={(row) => row.id}
        searchPlaceholder="Search this chain"
        fill
        // Nothing is ever done TO a custody line — it is append-only.
        selectable={false}
        auditColumns={false}
        // "MOVEMENT DETAIL" — product call, 10 Oct 2026 (was "Custody line"). Movement is the first column, so it opens it.
        rowPreview={{ title: 'Movement Detail' }}
        emptyMessage="This item has no custody lines."
        emptySearchMessage="No line matches that search."
      />
    </div>
  );
}

/** The Transferred rail's word for a pull-out, appended to its movement. */
const PULLED_OUT_TO: Partial<Record<EvidenceMovementType, string>> = {
  [MOVEMENT.transfer]: 'Court',
  [MOVEMENT.disposal]: 'Disposed',
};

function movementLabel(type: EvidenceMovementType): string {
  const to = PULLED_OUT_TO[type];
  return to ? `${MOVEMENT_LABEL[type]} · ${to}` : MOVEMENT_LABEL[type];
}

function chainColumns(names: Record<string, string>, canRead: boolean): DataColumn<EvidenceMovementItem>[] {
  return [
    /*
      NO LINE COLUMN (10 Oct 2026) — the row number already counts the chain. MOVEMENT is the anchor and carries
      WHERE a pull-out went, in the Transferred rail's own words (a transfer IS the Court state, a disposal Disposed).
    */
    { key: 'movementType', header: 'Movement', width: 240, render: (row) => <TextCell value={movementLabel(row.movementType)} /> },
    { key: 'recordedAt', header: 'Recorded', width: 190, render: (row) => <DateCell value={row.recordedAt} /> },
    { key: 'recordedByName', header: 'Recorded by', width: 170, render: (row) => <TextCell value={row.recordedByName} size="xs" /> },
    { key: 'from', header: 'From', width: 170, render: (row) => <TextCell value={row.fromLocationName} /> },
    { key: 'to', header: 'To', width: 200, render: (row) => <TextCell value={row.toLocationName ?? row.transferredTo} /> },
    // A panel column carries no permission of its own, so it is left out for a reader who cannot
    // resolve the name — HIDDEN, the way the index list gates its own personnel columns.
    ...(canRead
      ? [
          {
            key: 'custodian',
            header: 'Custodian after',
            width: 180,
            render: (row: EvidenceMovementItem) => <TextCell value={personnelLabel(row.toCustodianPersonnelId, names, canRead)} />,
          },
        ]
      : []),
    { key: 'reason', header: 'Reason', width: 200, summary: true, render: (row) => <TextCell value={row.reason} /> },
    { key: 'justification', header: 'Justification', width: 280, render: (row) => <TextCell value={row.justification} /> },
    {
      key: 'approval',
      header: 'Approved request',
      width: 150,
      render: (row) => <TextCell value={row.movementRequestId != null ? 'Yes' : 'Not required'} />,
    },
    {
      key: 'password',
      header: 'Password confirmed',
      width: 160,
      render: (row) => <TextCell value={row.passwordConfirmed ? 'Yes' : 'Not required'} />,
    },
    { key: 'attachments', header: 'Documents', width: 110, align: 'right', render: (row) => <TextCell value={String(row.attachmentCount)} mono numeric /> },
    { key: 'hash', header: 'Line hash', width: 220, render: (row) => <TextCell value={row.hash} mono /> },
  ];
}

function Documents({
  attachments,
  canDownload,
  onDownload,
}: {
  attachments: EvidenceAttachmentItem[];
  canDownload: boolean;
  onDownload: (attachment: EvidenceAttachmentItem) => void;
}) {
  const fetchPage = useCallback(async () => ({ items: attachments, total: attachments.length }), [attachments]);
  const state = useDataTable<EvidenceAttachmentItem>({ fetchPage, pageSize: 25 });

  return (
    <div className="flex h-full min-w-0 flex-col p-4">
      <DataTablePanel
        id="evidence-item-documents"
        state={state}
        columns={DOCUMENT_COLUMNS}
        rowKey={(row) => row.id}
        searchPlaceholder="Search these documents"
        fill
        selectable={false}
        otherInfo={(row) => ({ createdAt: row.createdAt, createdByName: row.createdByName, updatedAt: null, updatedByName: null })}
        rowActions={(row) =>
          canDownload
            ? [
                {
                  key: `download-${row.id}`,
                  label: 'Download',
                  icon: <Download className="h-4 w-4" aria-hidden="true" />,
                  permission: 'evidence-items.download',
                  onClick: () => onDownload(row),
                },
              ]
            : []
        }
        emptyMessage="No documents have been filed with this item."
        emptySearchMessage="No document matches that search."
      />
    </div>
  );
}

const DOCUMENT_COLUMNS: DataColumn<EvidenceAttachmentItem>[] = [
  { key: 'fileName', header: 'File', width: 280, render: (row) => <TextCell value={row.fileName} /> },
  { key: 'movementSequence', header: 'Filed with line', width: 140, render: (row) => <TextCell value={String(row.movementSequence)} mono /> },
  { key: 'contentType', header: 'Type', width: 200, summary: true, render: (row) => <TextCell value={row.contentType} mono /> },
  { key: 'sizeBytes', header: 'Size', width: 110, align: 'right', render: (row) => <TextCell value={formatBytes(row.sizeBytes)} mono /> },
  { key: 'sha256', header: 'Fingerprint (SHA-256)', width: 260, render: (row) => <TextCell value={row.sha256} mono /> },
];
